/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from "react";
import { Navigate, Route, Routes } from 'react-router';
import ServiceTermsVersion1 from "@pages/unauthenticated/service-terms/Version1";
import ServiceTermsVersion2 from "@pages/unauthenticated/service-terms/Version2";
import ServiceTermsVersion3 from "@pages/unauthenticated/service-terms/Version3";
import ServiceTermsVersion4 from "@pages/unauthenticated/service-terms/Version4";
import ServiceTermsVersion5 from "@pages/unauthenticated/service-terms/Version5";
import ServiceTermsVersionList from "@pages/unauthenticated/service-terms/VersionList";
import PrivacyPolicy from "@pages/unauthenticated/privacy-policy/PrivacyPolicy";
import PrivacyPolicyVersion2 from "@pages/unauthenticated/privacy-policy/PrivacyPolicyVersion2";
import PrivacyPolicyVersion3 from "@pages/unauthenticated/privacy-policy/PrivacyPolicyVersion3";
import PrivacyPolicyVersionList from "@pages/unauthenticated/privacy-policy/VersionList";
import CookiePolicy from "@pages/unauthenticated/cookie-policy/CookiePolicy";
import CookiePolicyVersionList from "@pages/unauthenticated/cookie-policy/VersionList";
import DataStorageTerms from "@pages/unauthenticated/data-storage-terms/DataStorageTerms";
import DataStorageTermsVersionList from "@pages/unauthenticated/data-storage-terms/VersionList";
import AcceptableUsePolicy from "@pages/unauthenticated/acceptable-use-policy/AcceptableUsePolicy";
import AcceptableUsePolicyVersionList from "@pages/unauthenticated/acceptable-use-policy/VersionList";
import {
  getDocumentConfig,
  getLatestVersion,
} from "@shared/utils/documentVersions";
import type { DocumentType } from "@shared/utils/documentVersions";

/**
 * Builds a `<Routes>` element for a versioned document (terms/policy) type,
 * covering the index (version list), `latest` redirect, per-version routes,
 * and a fallback redirect — identical skeleton shared by every document type.
 */
const createDocumentVersionRoutes = (
  docType: DocumentType,
  versionComponents: Record<number, React.ReactElement>,
  VersionListComponent: React.ComponentType,
) => {
  const config = getDocumentConfig(docType);
  const latestVersion = getLatestVersion(docType);

  return (
    <Routes>
      <Route index element={<VersionListComponent />} />
      <Route path="latest" element={<Navigate to={`${config.basePath}/v${latestVersion}`} replace />} />
      {config.versions.map((versionInfo) => {
        const component = versionComponents[versionInfo.version];
        if (!component) {
          return null;
        }
        return (
          <Route key={versionInfo.version} path={`v${versionInfo.version}`} element={component} />
        );
      })}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

/**
 * Routes for the service terms document versions.
 */
export const ServiceTermsRoutes = () => createDocumentVersionRoutes(
  'service-terms',
  {
    1: <ServiceTermsVersion1 />,
    2: <ServiceTermsVersion2 />,
    3: <ServiceTermsVersion3 />,
    4: <ServiceTermsVersion4 />,
    5: <ServiceTermsVersion5 />,
  },
  ServiceTermsVersionList,
);

/**
 * Routes for the privacy policy document versions.
 */
export const PrivacyPolicyRoutes = () => createDocumentVersionRoutes(
  'privacy-policy',
  {
    1: <PrivacyPolicy />,
    2: <PrivacyPolicyVersion2 />,
    3: <PrivacyPolicyVersion3 />,
  },
  PrivacyPolicyVersionList,
);

/**
 * Routes for the cookie policy document versions.
 */
export const CookiePolicyRoutes = () => createDocumentVersionRoutes(
  'cookie-policy',
  {
    1: <CookiePolicy />,
    2: <CookiePolicy />,
  },
  CookiePolicyVersionList,
);

/**
 * Routes for the data storage terms document versions.
 */
export const DataStorageTermsRoutes = () => createDocumentVersionRoutes(
  'data-storage-terms',
  {
    1: <DataStorageTerms />,
  },
  DataStorageTermsVersionList,
);

/**
 * Routes for the acceptable use policy document versions.
 */
export const AcceptableUsePolicyRoutes = () => createDocumentVersionRoutes(
  'acceptable-use-policy',
  {
    1: <AcceptableUsePolicy />,
  },
  AcceptableUsePolicyVersionList,
);
